import { Button, Radio, Typography } from '@mui/material';
import {
  athleteDisplayName,
  CompletionStatus,
  PublishedWorkoutStatusDomain,
  WorkoutStatus,
} from '@strength/domain';
import { mergeAthleteWorkoutLayers } from '@strength/domain-workout';
import { MdWarning } from '@teamworksdev/icons';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation } from '../../../../../../loco-sync';
import { trpc } from '../../../../../../trpc/react';
import {
  Drawer,
  DrawerFooter,
  DrawerHeader,
} from '../../../../../components/drawer';
import { toast } from '../../../../../hooks/useToast';
import { createActualMutationFns } from '../../../actuals-mutations';
import { useTabletContext } from '../../context/TabletContext';
import { WorkoutData } from '../../types';

export const EndSessionDrawer = ({
  athleteWorkoutLayers,
  open,
  onClose,
  onExit,
  unrecordedActuals,
}: {
  athleteWorkoutLayers: WorkoutData[];
  open: boolean;
  onClose: () => void;
  onExit: () => void;
  unrecordedActuals: string[];
}) => {
  const { t } = useTranslation();
  const { athleteSession, selectedAthletes } = useTabletContext();

  // The session spans athletes from MULTIPLE teams, each with its own
  // `warnOnMissingActuals` setting. `warnByTeam` below resolves the flag against
  // each athlete's OWN team rather than one arbitrary team's flag.
  const { data: warnRows } = trpc.tablet.warnOnMissingActuals.useQuery(
    // Header-only endpoint: scope comes from `tw-team-ids`; the fetch is gated on
    // having selected athletes. `warnByTeam` below resolves each athlete's own team.
    undefined,
    { staleTime: 0, enabled: selectedAthletes.length > 0 },
  );

  const warnByTeam = useMemo(() => {
    const byTeam = new Map(warnRows?.map((row) => [row.teamId, row.warn]));
    return (teamId: string) => byTeam.get(teamId) ?? false;
  }, [warnRows]);

  const initialAthleteStatuses = useMemo(() => {
    const layerMap = new Map(
      athleteWorkoutLayers.map((layer) => [
        `${layer.profileId}-${layer.workoutId}`,
        layer,
      ]),
    );

    return selectedAthletes.reduce(
      (acc, athlete) => {
        const workoutId = athleteSession[athlete.id]?.workoutId;
        const key = `${athlete.id}-${workoutId}`;
        acc[athlete.id] = layerMap.get(key)?.status || 'planned';
        return acc;
      },
      {} as Record<string, WorkoutStatus | undefined>,
    );
  }, [athleteWorkoutLayers, selectedAthletes, athleteSession]);

  const [athleteStatuses, setAthleteStatuses] = useState<
    Record<string, WorkoutStatus | undefined>
  >(initialAthleteStatuses);

  // Reset athlete statuses when drawer opens or dependencies change
  useEffect(() => {
    if (open) {
      setAthleteStatuses(initialAthleteStatuses);
    }
  }, [open, initialAthleteStatuses]);

  const mutation = useMutation();

  const athletesWithWorkouts = selectedAthletes.filter(
    (athlete) => athleteSession[athlete.id]?.workoutId,
  );

  const athletesWithoutWorkouts = selectedAthletes.filter(
    (athlete) => !athleteSession[athlete.id]?.workoutId,
  );

  const handleStatusChange = (athleteId: string, status: CompletionStatus) => {
    setAthleteStatuses((prev) => ({
      ...prev,
      [athleteId]: status,
    }));
  };

  const handleEndSession = () => {
    try {
      for (const athlete of athletesWithWorkouts) {
        const status = athleteStatuses[athlete.id];
        if (!status || status === 'canceled') continue;

        const athleteWorkoutLayer = athleteWorkoutLayers.find(
          (layer) =>
            layer.profileId === athlete.id &&
            layer.workoutId === athleteSession[athlete.id]?.workoutId,
        );
        if (!athleteWorkoutLayer) continue;

        const workout = mergeAthleteWorkoutLayers(
          athleteWorkoutLayer.blockLayer,
          athleteWorkoutLayer,
          athleteWorkoutLayer.modifier,
          athleteWorkoutLayer.actual,
        );

        if (!workout) continue;

        if (status !== 'planned') {
          const mutationFns = createActualMutationFns({
            actualLayer: athleteWorkoutLayer.actual ?? undefined,
            blockLayer: athleteWorkoutLayer.blockLayer ?? undefined,
            modifierLayer: athleteWorkoutLayer.modifier ?? undefined,
            athleteLayer: athleteWorkoutLayer,
            mutation,
          });

          mutationFns.onChangeWorkoutStatus(
            {
              blockId: athleteWorkoutLayer.blockId,
              profileId: athlete.id,
              athleteVersion: athleteWorkoutLayer.version,
              blockVersion: athleteWorkoutLayer.blockVersion,
              workoutStatus: status,
              blockLayerId: athleteWorkoutLayer.blockLayer?.id,
              athleteLayerId: athleteWorkoutLayer.id,
              actualLayerId: athleteWorkoutLayer.actual?.id ?? '',
            },
            workout.workoutId,
            workout,
            status,
          );
        }

        const updateWorkoutStatusCommand: PublishedWorkoutStatusDomain.Command =
          {
            module: PublishedWorkoutStatusDomain.Module,
            type: 'UpdateAthleteWorkoutStatus',
            input: {
              blockId: athleteWorkoutLayer.blockId,
              workoutId: athleteWorkoutLayer.workoutId,
              timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
              updates: [
                {
                  profileId: athlete.id,
                  status: status,
                  workout: workout,
                },
              ],
            },
          };

        mutation.mutate(updateWorkoutStatusCommand);
      }
      toast({
        title: t('sessionEnded'),
        severity: 'success',
      });
      onExit();
      onClose();
    } catch (error) {
      console.error('Error ending session:', error);
    }
  };

  return (
    <Drawer open={open} onClose={onClose}>
      <DrawerHeader onClose={onClose}>
        <Typography variant="h3">{t('sessionReview')}</Typography>
        <Typography variant="body2" color="text.secondary">
          {t('pleaseRecordAthleteAttendance')}
        </Typography>
      </DrawerHeader>

      <div className="flex flex-col gap-4 mx-6 overflow-y-auto grow ">
        <div className="space-y-2">
          {/* Header Row */}
          <div className="flex flex-row border-b border-gray-300 pb-2">
            <Typography
              variant="body1"
              className="font-semibold text-sm text-black/60 flex-1"
            >
              {t('athlete')}
            </Typography>

            <div className="flex flex-row gap-2">
              <StatusHeader label={t('completed')} />
              <StatusHeader label={t('skipped')} />
              <StatusHeader label={t('planned')} />
            </div>
          </div>

          {/* Athlete Rows */}
          {athletesWithWorkouts.map((athlete) => (
            <div key={athlete.id} className="flex flex-row items-center">
              <div className="flex flex-col flex-grow items-start">
                <Typography
                  variant="body1"
                  className="font-medium flex-1 text-sm"
                >
                  {athleteDisplayName(athlete)}
                </Typography>
                {warnByTeam(athlete.teamId) &&
                  unrecordedActuals.includes(athlete.id) && (
                    <div className="flex flex-row items-center text-warning-800">
                      <MdWarning />
                      <Typography
                        variant="captionMedium"
                        className="ml-1 text-warning-800"
                      >
                        {t('incompleteActuals')}
                      </Typography>
                    </div>
                  )}
              </div>
              <div className="flex flex-row gap-2">
                <StatusRadio
                  checked={athleteStatuses[athlete.id] === 'completed'}
                  onChange={() => handleStatusChange(athlete.id, 'completed')}
                  value="completed"
                  name={`status-${athlete.id}`}
                />
                <StatusRadio
                  checked={athleteStatuses[athlete.id] === 'skipped'}
                  onChange={() => handleStatusChange(athlete.id, 'skipped')}
                  value="skipped"
                  name={`status-${athlete.id}`}
                />
                <StatusRadio
                  checked={athleteStatuses[athlete.id] === 'planned'}
                  onChange={() => handleStatusChange(athlete.id, 'planned')}
                  value="planned"
                  name={`status-${athlete.id}`}
                />
              </div>
            </div>
          ))}
          <div className="flex flex-col pt-4">
            <Typography
              variant="body1"
              className="font-semibold text-sm text-black/60 flex-1 border-b border-gray-300 pb-2"
            >
              {t('noWorkoutSelected')}
            </Typography>

            {athletesWithoutWorkouts.map((athlete) => (
              <div key={athlete.id}>
                <div className="flex flex-row py-2 items-center">
                  <Typography
                    variant="body1"
                    className="font-medium flex-1 text-sm"
                  >
                    {athleteDisplayName(athlete)}
                  </Typography>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <DrawerFooter className="flex flex-row gap-2 w-full mt-4 p-4 justify-end">
        <Button onClick={onClose} variant="outlined">
          {t('cancel')}
        </Button>
        <Button onClick={handleEndSession} variant="contained">
          {t('endSession')}
        </Button>
      </DrawerFooter>
    </Drawer>
  );
};

const StatusHeader = ({
  label,
  className = '',
}: {
  label: string;
  className?: string;
}) => (
  <div className={`w-[75px] flex justify-center ${className}`}>
    <Typography
      variant="body1"
      className="font-semibold text-center text-sm text-black/60"
    >
      {label}
    </Typography>
  </div>
);

const StatusRadio = ({
  checked,
  onChange,
  value,
  name,
}: {
  checked: boolean;
  onChange: () => void;
  value: string;
  name: string;
}) => (
  <div className="w-[75px] flex justify-center">
    <Radio
      checked={checked}
      onChange={onChange}
      value={value}
      name={name}
      color="primary"
    />
  </div>
);
